import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Activity, ChevronDown, RefreshCw, Zap } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  type BillingCycle,
  BillingCycleSelector,
} from '@/features/billing/components/BillingCycleSelector'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import { UsageChart } from '@/shared/components/charts/UsageChart'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Calendar as CalendarComponent } from '@/shared/components/ui/calendar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { type Customer, customerService } from '../../services/customerService'
import type {
  AgentUsage,
  SignalUsage,
  UsageData,
} from '../../types/customerDetail.types'
import { getPeriodDates, getTimeRangeLabel } from '../../utils/periodDates'

interface UsageTabProps {
  customer: Customer
}

export function UsageTab({ customer }: UsageTabProps) {
  const queryClient = useQueryClient()
  const { currentEnvironment } = useEnvironment()
  const [period, setPeriod] = useState<BillingCycle>('current')
  const [selectedAgent, setSelectedAgent] = useState<string>('all')
  const [customFromDate, setCustomFromDate] = useState<Date | undefined>()
  const [customToDate, setCustomToDate] = useState<Date | undefined>()
  const [customFromOpen, setCustomFromOpen] = useState(false)
  const [customToOpen, setCustomToOpen] = useState(false)

  const { fromDate, toDate } = useMemo(
    () => getPeriodDates(period, customFromDate, customToDate),
    [period, customFromDate, customToDate]
  )

  const agentFilter = selectedAgent !== 'all' ? selectedAgent : undefined

  const agentUsageQuery = useQuery({
    queryKey: [
      'customer-usage',
      customer.id,
      currentEnvironment?.id,
      fromDate,
      toDate,
      agentFilter,
      'agents',
    ],
    queryFn: () =>
      customerService.getCustomerAgentUsage(
        customer.id,
        fromDate,
        toDate,
        agentFilter
      ),
  })

  const dailyUsageQuery = useQuery({
    queryKey: [
      'customer-usage',
      customer.id,
      currentEnvironment?.id,
      fromDate,
      toDate,
      agentFilter,
      'daily',
    ],
    queryFn: () =>
      customerService.getCustomerDailyUsage(customer.id, fromDate, toDate),
  })

  const loading = agentUsageQuery.isLoading || dailyUsageQuery.isLoading

  const agentUsage: AgentUsage[] = useMemo(
    () =>
      (agentUsageQuery.data?.agents ?? []).map(agent => ({
        agent_id: agent.agent_id,
        agent_name: agent.agent_name,
        tasks_completed: agent.tasks_completed,
        workflows_completed: agent.workflows_completed,
        outcomes_achieved: agent.outcomes_achieved,
        total_cost: agent.total_cost,
        total_revenue: agent.total_revenue,
        margin: agent.total_revenue - agent.total_cost,
        last_activity: agent.last_activity ?? new Date().toISOString(),
        signals: agent.signals.map(s => ({
          signal_id: s.signal_id,
          signal_name: s.signal_name,
          signal_kind: s.signal_kind,
          event_count: s.event_count,
          total_value: s.total_value,
          unit_price: s.unit_price,
          cost: s.cost,
        })),
      })),
    [agentUsageQuery.data]
  )

  const usageData: UsageData[] = useMemo(
    () =>
      (dailyUsageQuery.data?.daily ?? []).map(d => ({
        date: d.date,
        tasks: d.tasks,
        workflows: d.workflows,
        outcomes: d.outcomes,
        cost: d.cost,
        revenue: d.revenue,
      })),
    [dailyUsageQuery.data]
  )

  const handleRefresh = () => {
    queryClient.invalidateQueries({
      queryKey: ['customer-usage', customer.id],
    })
  }

  const getTotalTasks = () => {
    return agentUsage.reduce((sum, agent) => sum + agent.tasks_completed, 0)
  }

  const getTotalOutcomes = () => {
    return agentUsage.reduce((sum, agent) => sum + agent.outcomes_achieved, 0)
  }

  const getSignalKindIcon = (kind: 'task' | 'workflow' | 'outcome') => {
    switch (kind) {
      case 'task':
        return <Zap className='w-4 h-4 text-slate-500' />
      case 'workflow':
        return <Activity className='w-4 h-4 text-purple-500' />
      case 'outcome':
        return <Zap className='w-4 h-4 text-slate-500' />
      default:
        return <Activity className='w-4 h-4' />
    }
  }

  const getSignalKindColor = (kind: 'task' | 'workflow' | 'outcome') => {
    switch (kind) {
      case 'task':
        return 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
      case 'workflow':
        return 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200 dark:border-purple-600'
      case 'outcome':
        return 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600'
    }
  }

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header Skeleton */}
        <div className='flex justify-between items-center'>
          <div className='animate-pulse'>
            <div className='h-8 bg-slate-200 dark:bg-slate-800 rounded w-48 mb-2'></div>
            <div className='h-4 bg-slate-200 dark:bg-slate-800 rounded w-80'></div>
          </div>
          <div className='animate-pulse flex gap-2'>
            <div className='h-10 bg-slate-200 dark:bg-slate-800 rounded w-32'></div>
            <div className='h-10 bg-slate-200 dark:bg-slate-800 rounded w-48'></div>
            <div className='h-10 bg-slate-200 dark:bg-slate-800 rounded w-32'></div>
          </div>
        </div>

        {/* Cards Skeleton */}
        <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
          {Array.from({ length: 4 }).map((_, i) => (
            <Card
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              key={i}
              className='bg-transparent border h-24 flex items-center'
            >
              <CardContent className='px-3 py-0 w-full'>
                <div className='animate-pulse'>
                  <div className='h-4 bg-slate-200 dark:bg-slate-800 rounded w-20 mb-1'></div>
                  <div className='h-6 bg-slate-200 dark:bg-slate-800 rounded w-16'></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Chart Skeleton */}
        <Card className='bg-transparent border'>
          <CardContent className='p-6'>
            <div className='animate-pulse'>
              <div className='h-6 bg-slate-200 dark:bg-slate-800 rounded w-1/3 mb-4'></div>
              <div className='h-64 bg-slate-200 dark:bg-slate-800 rounded'></div>
            </div>
          </CardContent>
        </Card>

        {/* Most Used Agents Skeleton */}
        <Card className='bg-transparent border'>
          <CardHeader>
            <div className='animate-pulse'>
              <div className='h-6 bg-slate-200 dark:bg-slate-800 rounded w-32 mb-2'></div>
              <div className='h-4 bg-slate-200 dark:bg-slate-800 rounded w-48'></div>
            </div>
          </CardHeader>
          <CardContent>
            <div className='animate-pulse space-y-4'>
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  key={i}
                  className='h-20 bg-slate-200 dark:bg-slate-800 rounded'
                ></div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Agent Details Skeleton */}
        <div className='space-y-4'>
          <div className='animate-pulse'>
            <div className='h-6 bg-slate-200 dark:bg-slate-800 rounded w-48 mb-4'></div>
          </div>
          {Array.from({ length: 2 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Card key={i} className='bg-transparent border'>
              <CardContent className='p-6'>
                <div className='animate-pulse space-y-4'>
                  <div className='h-16 bg-slate-200 dark:bg-slate-800 rounded'></div>
                  <div className='grid grid-cols-4 gap-4'>
                    {Array.from({ length: 4 }).map((_, j) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={j}
                        className='h-12 bg-slate-200 dark:bg-slate-800 rounded'
                      ></div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h2 className='text-2xl font-bold'>Usage Analytics</h2>
          <p className='text-muted-foreground'>
            Detailed usage tracking and metrics for all agents
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button variant='outline' size='sm' onClick={handleRefresh}>
            <RefreshCw className='w-4 h-4' />
          </Button>
        </div>
      </div>

      {/* Period and Agent Selectors */}
      <div className='flex justify-start items-center gap-4'>
        <BillingCycleSelector
          value={period}
          onValueChange={setPeriod}
          className='w-48'
        />

        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
          <SelectTrigger className='w-40'>
            <SelectValue placeholder='Select agent' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Agents</SelectItem>
            {agentUsage.map(agent => (
              <SelectItem key={agent.agent_id} value={agent.agent_id}>
                {agent.agent_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Custom Date Range Selectors */}
        {period === 'custom_range' && (
          <div className='flex items-center gap-2 ml-4 pl-4 border-l'>
            <span className='text-sm text-muted-foreground'>From:</span>
            <Popover open={customFromOpen} onOpenChange={setCustomFromOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-44 h-9 justify-between font-normal'
                >
                  {customFromDate
                    ? customFromDate.toLocaleDateString()
                    : 'Select date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <CalendarComponent
                  mode='single'
                  selected={customFromDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setCustomFromDate(date)
                    setCustomFromOpen(false)
                  }}
                />
              </PopoverContent>
            </Popover>
            <span className='text-muted-foreground text-sm'>to</span>
            <Popover open={customToOpen} onOpenChange={setCustomToOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-44 h-9 justify-between font-normal'
                >
                  {customToDate
                    ? customToDate.toLocaleDateString()
                    : 'Select date'}
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className='w-auto overflow-hidden p-0'
                align='start'
              >
                <CalendarComponent
                  mode='single'
                  selected={customToDate}
                  captionLayout='dropdown'
                  onSelect={date => {
                    setCustomToDate(date)
                    setCustomToOpen(false)
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Tasks</span>
              <span className='text-md font-bold'>
                {getTotalTasks().toLocaleString()}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              Across all agents
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Workflows</span>
              <span className='text-md font-bold'>
                {agentUsage
                  .reduce((sum, agent) => sum + agent.workflows_completed, 0)
                  .toLocaleString()}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              Completed workflows
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Total Outcomes</span>
              <span className='text-md font-bold'>
                {getTotalOutcomes().toLocaleString()}
              </span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>
              Successful results
            </p>
          </CardContent>
        </Card>

        <Card className='bg-transparent border h-24 flex items-center'>
          <CardContent className='px-3 py-0 w-full'>
            <div className='flex items-center justify-between'>
              <span className='text-md font-medium'>Active Agents</span>
              <span className='text-md font-bold'>{agentUsage.length}</span>
            </div>
            <p className='text-xs text-muted-foreground mt-1'>Currently used</p>
          </CardContent>
        </Card>
      </div>

      <div className='border-t mt-5 border-black'></div>

      {/* Usage Trends Chart */}
      <Card className='bg-transparent border'>
        <CardContent className='p-6'>
          <div className='mb-4'>
            <h3 className='text-lg font-semibold'>
              Usage Trends ({getTimeRangeLabel(period)})
            </h3>
            {selectedAgent !== 'all' && (
              <p className='text-sm text-muted-foreground'>
                Filtered by:{' '}
                {agentUsage.find(a => a.agent_id === selectedAgent)
                  ?.agent_name || 'Unknown Agent'}
              </p>
            )}
          </div>
          <UsageChart data={usageData} height={350} />
        </CardContent>
      </Card>

      <div className='border-t mt-5 border-black'></div>

      {/* Daily Usage by Signal */}
      <div className='space-y-4'>
        <h3 className='text-lg font-semibold text-slate-600'>
          Daily usage by signal
        </h3>

        {/* Enhanced Signal Usage Table */}

        {(() => {
          // Build table data: flatten all signals across all agents
          const allSignals: (SignalUsage & { agent_name: string })[] = []
          agentUsage.forEach(agent => {
            agent.signals.forEach(signal => {
              allSignals.push({ ...signal, agent_name: agent.agent_name })
            })
          })

          // Build a lookup: date → total events that day (tasks+workflows+outcomes)
          const dailyLookup = new Map<string, number>()
          usageData.forEach(d => {
            dailyLookup.set(d.date, d.tasks + d.workflows + d.outcomes)
          })

          // Last 7 days available in usageData
          const recentDays = usageData.slice(-7)

          const dateColumns: ColumnDef<SignalUsage & { agent_name: string }>[] =
            recentDays.map(d => ({
              accessorKey: d.date,
              header: new Date(`${d.date}T12:00:00`)
                .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                .toUpperCase(),
              cell: () => {
                const count = dailyLookup.get(d.date) ?? 0
                return (
                  <div
                    className={
                      count > 0 ? 'font-medium' : 'text-muted-foreground'
                    }
                  >
                    {count.toLocaleString()}
                  </div>
                )
              },
              size: 100,
            }))

          const columns: ColumnDef<SignalUsage & { agent_name: string }>[] = [
            {
              accessorKey: 'signal_name',
              header: 'EVENTS',
              cell: ({ row }) => (
                <div className='flex items-center gap-2 font-medium'>
                  <div className='w-2 h-2 bg-orange-500 rounded-full'></div>
                  <div className='flex flex-col'>
                    <span className='capitalize'>
                      {row.original.signal_name.replace(/_/g, ' ')}
                    </span>
                    <div className='flex items-center gap-2 mt-1'>
                      <Badge
                        variant='outline'
                        className={`${getSignalKindColor(row.original.signal_kind)} text-xs px-1 py-0`}
                      >
                        {row.original.signal_kind}
                      </Badge>
                      <span className='text-xs text-muted-foreground'>
                        {row.original.agent_name}
                      </span>
                    </div>
                  </div>
                </div>
              ),
              size: 250,
            },
            {
              accessorKey: 'event_count',
              header: 'TOTAL',
              cell: ({ row }) => (
                <div className='font-medium'>
                  {row.original.event_count.toLocaleString()}
                </div>
              ),
              size: 100,
            },
            ...dateColumns,
          ]

          return (
            <div className='[&_table]:bg-transparent [&_thead]:bg-slate-50 dark:[&_thead]:bg-slate-800 [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
              <EnhancedTable
                data={allSignals}
                columns={columns}
                height='300px'
                className='bg-transparent border-0'
              />
            </div>
          )
        })()}
      </div>

      <div className='border-t mt-5 border-black'></div>

      {/* Agent Cards Container */}
      <div className='space-y-4'>
        <h3 className='text-lg font-semibold text-slate-600'>Agents</h3>
        {agentUsage.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No agent activity in the selected period.
          </p>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {agentUsage.map(agent => (
              <Card key={agent.agent_id} className='bg-transparent border'>
                <CardHeader>
                  <CardTitle>{agent.agent_name}</CardTitle>
                  <CardDescription>Agent performance metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className='space-y-4'>
                    {/* Agent Summary */}
                    <div className='grid grid-cols-3 gap-2'>
                      <div className='bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-center'>
                        <div className='text-md font-bold text-slate-700 dark:text-slate-300'>
                          {agent.tasks_completed.toLocaleString()}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          Tasks
                        </div>
                      </div>
                      <div className='bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-center'>
                        <div className='text-md font-bold text-slate-600 dark:text-slate-400'>
                          {agent.workflows_completed.toLocaleString()}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          Workflows
                        </div>
                      </div>
                      <div className='bg-slate-100 dark:bg-slate-800 p-3 rounded-lg text-center'>
                        <div className='text-md font-bold text-slate-800 dark:text-slate-200'>
                          {agent.outcomes_achieved.toLocaleString()}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          Outcomes
                        </div>
                      </div>
                    </div>

                    {/* Signal Breakdown */}
                    <div>
                      <h4 className='font-medium mb-3'>Signal Breakdown</h4>
                      <div className='space-y-2'>
                        {agent.signals.map(signal => (
                          <div
                            key={signal.signal_id}
                            className='flex items-center justify-between p-3 border rounded-lg'
                          >
                            <div className='flex items-center gap-3'>
                              {getSignalKindIcon(signal.signal_kind)}
                              <div>
                                <div className='text-md font-medium capitalize'>
                                  {signal.signal_name.replace(/_/g, ' ')}
                                </div>
                                <div className='flex items-center gap-2 mt-1'>
                                  <Badge
                                    variant='outline'
                                    className={getSignalKindColor(
                                      signal.signal_kind
                                    )}
                                  >
                                    {signal.signal_kind}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <div className='text-right'>
                              <div className='text-md font-bold'>
                                {signal.event_count.toLocaleString()}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                billable signals
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
